import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import { vendorStore } from "../vendors";
import type { ReviewMode } from "../org/types";
import type {
  Invoice,
  InvoiceDirection,
  InvoiceStatus,
  InvoiceStore,
  NewInvoiceInput,
} from "./types";

const BUCKET = "receipts";

interface InvoiceRow {
  id: string;
  user_id: string;
  counterparty: string;
  vendor_id: string | null;
  direction: InvoiceDirection;
  amount_cents: number;
  currency: string;
  issued_on: string;
  due_on: string | null;
  status: InvoiceStatus;
  notes: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

const ROW_COLUMNS =
  "id, user_id, counterparty, vendor_id, direction, amount_cents, currency, issued_on, due_on, status, notes, submitted_by, submitted_at, approved_by, approved_at, rejection_reason, created_at, updated_at";

function extFromName(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot === -1 || dot === name.length - 1) return "bin";
  const cleaned = name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8);
  return cleaned || "bin";
}

function rowToInvoice(row: InvoiceRow): Invoice {
  return {
    id: row.id,
    counterparty: row.counterparty,
    vendorId: row.vendor_id,
    direction: row.direction,
    amountCents: Number(row.amount_cents),
    currency: row.currency,
    issuedOn: row.issued_on,
    dueOn: row.due_on,
    status: row.status,
    notes: row.notes,
    createdBy: row.user_id,
    submittedBy: row.submitted_by,
    submittedAt: row.submitted_at,
    approvedBy: row.approved_by,
    approvedAt: row.approved_at,
    rejectionReason: row.rejection_reason,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function deriveStatus(input: NewInvoiceInput): InvoiceStatus {
  if (input.status) return input.status;
  if (input.direction === "incoming" && input.dueOn && input.dueOn < todayIso()) { return "overdue"; }
  return "pending";
}

// ---------------------------------------------------------------------------
// Wave 17 — review mode enforcement helpers (mirrors expenses/supabaseStore.ts)
// ---------------------------------------------------------------------------

interface ReviewContext {
  reviewMode: ReviewMode;
  isAdminOrOwner: boolean;
}

async function getReviewContext(orgId: string, userId: string): Promise<ReviewContext> {
  const [orgResult, memberResult] = await Promise.all([
    supabase.from("organizations").select("review_mode").eq("id", orgId).single(),
    supabase.from("org_members").select("role").eq("organization_id", orgId).eq("user_id", userId).eq("status", "active").single(),
  ]);
  const rawMode = (orgResult.data as { review_mode: string } | null)?.review_mode ?? "manual";
  const reviewMode: ReviewMode =
    rawMode === "auto_approve" || rawMode === "coming_soon" ? rawMode : "manual";
  const role = (memberResult.data as { role: string } | null)?.role ?? "employee";
  const isAdminOrOwner = role === "admin" || role === "owner";
  return { reviewMode, isAdminOrOwner };
}

function resolveInitialInvoiceStatus(baseStatus: InvoiceStatus, ctx: ReviewContext): InvoiceStatus {
  if (baseStatus !== "pending") return baseStatus;
  if (ctx.isAdminOrOwner || ctx.reviewMode === "auto_approve") {
    return "approved";
  }
  return baseStatus;
}

export function createSupabaseInvoiceStore(): InvoiceStore {
  const listeners = new Set<() => void>();
  let snapshot: Invoice[] = [];
  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void { for (const l of listeners) l(); }
  function commit(next: Invoice[]): void { snapshot = next; emit(); }
  function applyUpdate(updated: Invoice): void {
    const idx = snapshot.findIndex((i) => i.id === updated.id);
    if (idx !== -1) { const next = snapshot.slice(); next[idx] = updated; commit(next); }
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) { commit([]); return; }
    const orgId = getActiveOrganizationId();
    if (!orgId) { commit([]); return; }
    const { data: rows, error } = await supabase.from("invoices").select(ROW_COLUMNS).eq("organization_id", orgId).order("issued_on", { ascending: false }).order("created_at", { ascending: false });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[invoices] list failed: ${error.message}`);
      commit([]); return;
    }
    const typedRows = (rows ?? []) as InvoiceRow[];
    commit(typedRows.map((row) => rowToInvoice(row)));
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) { loadPromise = loadFromServer().finally(() => { initialized = true; loadPromise = null; }); }
    return loadPromise;
  }

  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") { initialized = false; commit([]); return; }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") { initialized = false; void ensureLoaded(); }
    });
    subscribeActiveOrg(() => { initialized = false; void ensureLoaded(); });
  }

  return {
    list(): Invoice[] { if (!initialized && !loadPromise) { void ensureLoaded(); } return snapshot; },
    get(id: string): Invoice | undefined { return snapshot.find((i) => i.id === id); },

    async add(input: NewInvoiceInput): Promise<Invoice> {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) { throw new Error("You must be signed in to create an invoice."); }
      const userId = userData.user.id;
      const orgId = getActiveOrganizationId();
      if (!orgId) { throw new Error("No active organization selected. Reload the page and try again."); }
      const invoiceId = crypto.randomUUID();
      const status = deriveStatus(input);
      let vendorId: string | null = null;
      if (input.counterparty && input.counterparty.trim()) {
        try {
          const vendor = await vendorStore.upsertByName(input.counterparty);
          vendorId = vendor.id;
        } catch {
          vendorId = null;
        }
      }
      const { data: inserted, error: insertError } = await supabase.from("invoices").insert({
        id: invoiceId, organization_id: orgId, user_id: userId,
        counterparty: input.counterparty, vendor_id: vendorId,
        direction: input.direction,
        amount_cents: input.amountCents, currency: input.currency ?? "USD",
        issued_on: input.issuedOn, due_on: input.dueOn ?? null,
        status, notes: input.notes ?? null,
      }).select(ROW_COLUMNS).single();
      if (insertError || !inserted) { throw new Error(`Failed to create invoice: ${insertError?.message ?? "unknown error"}`); }
      let row = inserted as InvoiceRow;

      // Wave 17 — enforce review mode.
      try {
        const ctx = await getReviewContext(orgId, userId);
        const resolved = resolveInitialInvoiceStatus(row.status as InvoiceStatus, ctx);
        if (resolved !== row.status) {
          const nowIso = new Date().toISOString();
          const { data: updatedRow } = await supabase
            .from("invoices")
            .update({
              status: resolved,
              submitted_by: userId,
              submitted_at: nowIso,
              approved_by: userId,
              approved_at: nowIso,
            })
            .eq("id", invoiceId)
            .select(ROW_COLUMNS)
            .single();
          if (updatedRow) row = updatedRow as InvoiceRow;
        }
      } catch {
        // Non-fatal: fall back to original status.
      }

      const invoice = rowToInvoice(row);
      initialized = true;
      commit([invoice, ...snapshot]);
      if (input.file) {
        const ext = extFromName(input.file.name);
        const receiptId = crypto.randomUUID();
        const storagePath = `${userId}/${receiptId}.${ext}`;
        const { error: uploadError } = await supabase.storage.from(BUCKET).upload(storagePath, input.file, { contentType: input.file.type || "application/octet-stream", upsert: false });
        if (uploadError) { throw new Error(`Invoice proof upload failed: ${uploadError.message}`); }
        const { error: receiptInsertError } = await supabase.from("receipts").insert({
          id: receiptId, organization_id: orgId, user_id: userId, filename: input.file.name,
          mime_type: input.file.type || "application/octet-stream", size_bytes: input.file.size,
          storage_path: storagePath, linked_expense_id: null, linked_invoice_id: invoiceId,
        });
        if (receiptInsertError) {
          await supabase.storage.from(BUCKET).remove([storagePath]);
          throw new Error(`Failed to record invoice proof: ${receiptInsertError.message}`);
        }
      }
      return invoice;
    },

    async addMany(inputs: NewInvoiceInput[]): Promise<Invoice[]> {
      if (inputs.length === 0) return [];
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) { throw new Error("You must be signed in to import invoices."); }
      const userId = userData.user.id;
      const orgId = getActiveOrganizationId();
      if (!orgId) { throw new Error("No active organization selected. Reload the page and try again."); }
      const vendorIds: Array<string | null> = [];
      for (const input of inputs) {
        if (!input.counterparty || !input.counterparty.trim()) { vendorIds.push(null); continue; }
        try {
          const vendor = await vendorStore.upsertByName(input.counterparty);
          vendorIds.push(vendor.id);
        } catch { vendorIds.push(null); }
      }
      const rowsToInsert = inputs.map((input, idx) => ({
        id: crypto.randomUUID(), organization_id: orgId, user_id: userId,
        counterparty: input.counterparty, vendor_id: vendorIds[idx] ?? null,
        direction: input.direction, amount_cents: input.amountCents,
        currency: input.currency ?? "USD", issued_on: input.issuedOn,
        due_on: input.dueOn ?? null, status: deriveStatus(input), notes: input.notes ?? null,
      }));
      const { data: inserted, error: insertError } = await supabase.from("invoices").insert(rowsToInsert).select(ROW_COLUMNS);
      if (insertError || !inserted) { throw new Error(`Failed to import invoices: ${insertError?.message ?? "unknown error"}`); }
      let typedRows = inserted as InvoiceRow[];
      // Wave 17 — enforce review mode for bulk imports.
      try {
        const ctx = await getReviewContext(orgId, userId);
        const idsToApprove = typedRows.filter((r) => resolveInitialInvoiceStatus(r.status as InvoiceStatus, ctx) !== r.status).map((r) => r.id);
        if (idsToApprove.length > 0) {
          const nowIso = new Date().toISOString();
          const { data: approvedRows } = await supabase.from("invoices").update({ status: "approved", submitted_by: userId, submitted_at: nowIso, approved_by: userId, approved_at: nowIso }).in("id", idsToApprove).select(ROW_COLUMNS);
          if (approvedRows) {
            const updatedMap = new Map((approvedRows as InvoiceRow[]).map((r) => [r.id, r]));
            typedRows = typedRows.map((r) => updatedMap.get(r.id) ?? r);
          }
        }
      } catch { /* Non-fatal: keep original statuses on failure. */ }
      const newInvoices = typedRows.map((row) => rowToInvoice(row));
      initialized = true;
      commit([...newInvoices, ...snapshot]);
      return newInvoices;
    },

    async update(id: string, patch: Partial<Omit<Invoice, "id" | "createdAt" | "updatedAt">>): Promise<Invoice> {
      const dbPatch: Record<string, unknown> = {};
      if (patch.counterparty !== undefined) dbPatch.counterparty = patch.counterparty;
      if (patch.direction !== undefined) dbPatch.direction = patch.direction;
      if (patch.amountCents !== undefined) dbPatch.amount_cents = patch.amountCents;
      if (patch.currency !== undefined) dbPatch.currency = patch.currency;
      if (patch.issuedOn !== undefined) dbPatch.issued_on = patch.issuedOn;
      if (patch.dueOn !== undefined) dbPatch.due_on = patch.dueOn;
      if (patch.status !== undefined) dbPatch.status = patch.status;
      if (patch.notes !== undefined) dbPatch.notes = patch.notes;
      const { data, error } = await supabase.from("invoices").update(dbPatch).eq("id", id).select(ROW_COLUMNS).single();
      if (error || !data) { throw new Error(`Failed to update invoice: ${error?.message ?? "unknown"}`); }
      const updated = rowToInvoice(data as InvoiceRow);
      applyUpdate(updated);
      return updated;
    },

    async setStatus(id: string, status: InvoiceStatus): Promise<Invoice> {
      const { data, error } = await supabase.from("invoices").update({ status }).eq("id", id).select(ROW_COLUMNS).single();
      if (error || !data) { throw new Error(`Failed to update status: ${error?.message ?? "unknown"}`); }
      const updated = rowToInvoice(data as InvoiceRow);
      applyUpdate(updated);
      return updated;
    },

    async remove(id: string): Promise<void> {
      const target = snapshot.find((i) => i.id === id);
      if (!target) return;
      const next = snapshot.filter((i) => i.id !== id);
      commit(next);
      void supabase.from("invoices").delete().eq("id", id).then(({ error }) => {
        if (error) { console.warn(`[invoices] delete failed: ${error.message}`); }
      });
    },

    async submit(id: string): Promise<Invoice> {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) { throw new Error("You must be signed in to submit an invoice."); }
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase.from("invoices").update({ status: "pending", submitted_by: userData.user.id, submitted_at: nowIso }).eq("id", id).select(ROW_COLUMNS).single();
      if (error || !data) { throw new Error(`Failed to submit invoice: ${error?.message ?? "unknown"}`); }
      const updated = rowToInvoice(data as InvoiceRow);
      applyUpdate(updated);
      return updated;
    },

    async approve(id: string): Promise<Invoice> {
      const { data, error } = await supabase.rpc("approve_invoice", { invoice_id: id, action: "approve", reason: null });
      if (error) { throw new Error(`Approve failed: ${error.message}`); }
      const updated = rowToInvoice(data as InvoiceRow);
      applyUpdate(updated);
      return updated;
    },

    async reject(id: string, reason: string): Promise<Invoice> {
      const { data, error } = await supabase.rpc("approve_invoice", { invoice_id: id, action: "reject", reason });
      if (error) { throw new Error(`Reject failed: ${error.message}`); }
      const updated = rowToInvoice(data as InvoiceRow);
      applyUpdate(updated);
      return updated;
    },

    subscribe(listener: () => void): () => void {
      attachAuthListener();
      if (!initialized && !loadPromise) { void ensureLoaded(); }
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}
